import React, { useState, useEffect } from "react";
import { MapPin, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const cities = [{
  name: "Austin"
}, {
  name: "Round Rock"
}, {
  name: "Georgetown"
}, {
  name: "Pflugerville"
}, {
  name: "Cedar Park"
}, {
  name: "Leander"
}, {
  name: "Hutto"
}, {
  name: "Liberty Hill"
}, {
  name: "Kyle"
}, {
  name: "Buda"
}, {
  name: "Lakeway"
}, {
  name: "Bee Cave"
}];
const serviceImages = [{
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562197/img/central_texas_map_stylized.png",
  alt: "Stylized map of Central Texas service areas"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562197/img/austin_skyline_clean.png",
  alt: "Clean Austin skyline view"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562197/img/suburban_home_exterior.png",
  alt: "Pristine suburban home in Round Rock"
}];
export default function ProudlyServingCentralTexas() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const [currentImage, setCurrentImage] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImage(prev => (prev + 1) % serviceImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);
  const nextImage = () => setCurrentImage(prev => (prev + 1) % serviceImages.length);
  const prevImage = () => setCurrentImage(prev => (prev - 1 + serviceImages.length) % serviceImages.length);
  return <section id="proudly-serving-central-texas" ref={ref} className={`dark bg-gradient-to-br from-[#F9CBD6] via-[#F2AFBC] to-[#9E182B] text-foreground py-24 relative overflow-hidden transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      {}
      <div className="absolute inset-0 bg-black/60 -z-10" />

      <div className="container mx-auto px-6 md:px-12 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {}
          <div className="relative w-full aspect-square max-w-md mx-auto lg:max-w-full lg:mx-0 flex items-center justify-center group/carousel">
            <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full transform scale-75" />
            
            <div className="relative z-10 w-full h-full overflow-hidden shadow-2xl">
              {serviceImages.map((img, i) => <img key={img.src} data-index={i} src={img.src} data-wvc-srcset={`${img.src}-wvc-srcset`} data-wvc-sizes={`${img.src}-wvc-sizes`} alt={img.alt} loading="lazy" className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${currentImage === i ? "opacity-100" : "opacity-0"}`} />)}
              
              {}
              <div className="absolute inset-x-0 bottom-4 flex justify-center gap-2 z-20">
                {serviceImages.map((_, i) => <Button key={i} data-index={i} variant="ghost" size="icon" onClick={() => setCurrentImage(i)} className={`h-1.5 p-0 min-w-0 transition-all rounded-none hover:bg-white ${currentImage === i ? "w-8 bg-white" : "w-2 bg-white/50"}`} />)}
              </div>

              {}
              <Button variant="ghost" size="icon" onClick={prevImage} className="absolute left-4 top-1/2 -translate-y-1/2 z-20 size-10 bg-black/20 hover:bg-black/40 text-white rounded-none opacity-0 group-hover/carousel:opacity-100 transition-opacity">
                <ChevronLeft className="size-6" />
              </Button>
              <Button variant="ghost" size="icon" onClick={nextImage} className="absolute right-4 top-1/2 -translate-y-1/2 z-20 size-10 bg-black/20 hover:bg-black/40 text-white rounded-none opacity-0 group-hover/carousel:opacity-100 transition-opacity">
                <ChevronRight className="size-6" />
              </Button>
            </div>
            
            {}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full pointer-events-none z-20">
               <div className="absolute top-[45%] left-[48%] w-4 h-4 bg-white rounded-full animate-ping opacity-20" />
               <div className="absolute top-[38%] left-[52%] w-3 h-3 bg-white rounded-full animate-ping opacity-20 delay-300" />
               <div className="absolute top-[52%] left-[45%] w-3 h-3 bg-white rounded-full animate-ping opacity-20 delay-700" />
            </div>
          </div>

          {}
          <div className="flex flex-col space-y-10">
            <div className="space-y-4">
              <div className="inline-flex flex-col bg-primary text-white px-4 py-2 text-xs font-bold tracking-widest uppercase mb-2">
                <span>Owner Operated</span>
                <span className="opacity-80">Local Pride</span>
              </div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter text-white leading-[1.1]">
                PROUDLY SERVING <br />
                <span className="text-white/90">CENTRAL TEXAS</span>
              </h2>
              <p className="text-lg text-white/80 max-w-lg leading-relaxed">
                From downtown Austin to the surrounding suburbs, we bring pure effort and precision to every property we touch.
              </p>
            </div>

            {}
            <div className="grid grid-cols-2 gap-x-4 gap-y-4">
              {cities.map((city, i) => <div key={city.name} data-index={i} className="flex items-center space-x-3 group">
                  <MapPin className="w-5 h-5 text-white/70 group-hover:text-white transition-colors shrink-0" />
                  <span className="text-xl font-medium text-white/90 group-hover:text-white transition-colors">
                    {city.name}
                  </span>
                </div>)}
            </div>

            {}
            <div className="pt-4">
              <Link to="/PureDetailBookingPage">
                <Button size="lg" className="bg-primary hover:bg-secondary text-white rounded-none px-10 py-7 text-lg font-semibold tracking-wide shadow-xl hover:shadow-2xl transition-all duration-300 group border-none">
                  Check Availability in Your Area
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-96 bg-gradient-to-t from-[#9E182B] via-[#9E182B]/40 to-transparent pointer-events-none z-0" />
    </section>;
}